import QRCode from 'qrcode';

/**
 * Render and print a tire hotel storage label (varastotarra).
 *
 * Drawn to a canvas at the Zebra ZD220's native 203 DPI on a 102x74mm label, then
 * printed as a flat image so no CSS rotation tricks are involved. Shared by the card
 * detail page and the tire swap dialog, which prints the set that just went into storage.
 */
export default async function printTireLabel(card, org, t, landscape = true) {
  const typeMap = {
    winter: t('tire_hotel.winter'),
    summer: t('tire_hotel.summer'),
    all_season: t('tire_hotel.all_season'),
  };
  const rimMap = {
    steel: t('tire_hotel.steel'),
    alloy: t('tire_hotel.alloy'),
    tires_only: t('tire_hotel.tires_only'),
  };

  const reg = card.vehicle_registration_number || '';
  const vehicleDisplay = card.vehicle_display || '';
  const sepIdx = vehicleDisplay.indexOf(' - ');
  const vehicleMakeModel = sepIdx >= 0 ? vehicleDisplay.substring(sepIdx + 3) : '';
  const customer = card.customer_name || '';
  const tireInfo = [card.tire_brand, card.tire_model, card.tire_size_display].filter(Boolean).join(' ');
  const tireType = typeMap[card.tire_set_type] || card.tire_set_type || '';
  const studdedText = card.studded ? ` · ${t('tire_hotel.studded')}` : '';
  const rimText = rimMap[card.rim_type] || '';
  const typeLine = [tireType + studdedText, rimText].filter(Boolean).join(' · ');
  const location = card.location_label || '-';
  const storedDate = card.storage_start_date ? new Date(card.storage_start_date).toLocaleDateString('fi-FI') : '';
  const workshop = org?.organization_name || '';

  /* Render label to canvas at Zebra ZD220 native 203 DPI.
     Label is 102×74mm. We draw in landscape, then the image is printed
     as-is — no CSS rotation tricks, just a flat image. */
  const DPI = 203;
  const MM_TO_PX = DPI / 25.4;
  const W = Math.round(102 * MM_TO_PX); // 815 px
  const H = Math.round(74 * MM_TO_PX);  // 591 px
  const PAD = Math.round(5 * MM_TO_PX); // 5mm padding

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // White background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#000000';

  let y = PAD;

  // Registration number
  ctx.font = `900 ${Math.round(46 * MM_TO_PX / 2.83)}px Arial, Helvetica, sans-serif`;
  ctx.fillText(reg, PAD, y + ctx.measureText('M').actualBoundingBoxAscent);
  y += ctx.measureText('M').actualBoundingBoxAscent + Math.round(3.5 * MM_TO_PX);

  // Vehicle make/model
  if (vehicleMakeModel) {
    ctx.font = `600 ${Math.round(18 * MM_TO_PX / 2.83)}px Arial, Helvetica, sans-serif`;
    ctx.fillStyle = '#333333';
    ctx.fillText(vehicleMakeModel, PAD, y + ctx.measureText('M').actualBoundingBoxAscent);
    y += ctx.measureText('M').actualBoundingBoxAscent + Math.round(2.5 * MM_TO_PX);
    ctx.fillStyle = '#000000';
  }

  // Customer name
  ctx.font = `600 ${Math.round(18 * MM_TO_PX / 2.83)}px Arial, Helvetica, sans-serif`;
  ctx.fillText(customer, PAD, y + ctx.measureText('M').actualBoundingBoxAscent);
  y += ctx.measureText('M').actualBoundingBoxAscent + Math.round(3.5 * MM_TO_PX);

  // Tire info. A staggered set carries two sizes, so shrink the line to fit the label
  // rather than let it run off the edge.
  const infoFont = (px) => `400 ${px}px Arial, Helvetica, sans-serif`;
  const infoPx = Math.round(14 * MM_TO_PX / 2.83);
  let tireInfoPx = infoPx;
  ctx.font = infoFont(tireInfoPx);
  while (tireInfoPx > infoPx * 0.7 && ctx.measureText(tireInfo).width > W - 2 * PAD) {
    tireInfoPx -= 1;
    ctx.font = infoFont(tireInfoPx);
  }
  ctx.fillText(tireInfo, PAD, y + ctx.measureText('M').actualBoundingBoxAscent);
  y += ctx.measureText('M').actualBoundingBoxAscent + Math.round(2 * MM_TO_PX);
  ctx.font = infoFont(infoPx);

  // Type line
  ctx.fillStyle = '#333333';
  ctx.fillText(typeLine, PAD, y + ctx.measureText('M').actualBoundingBoxAscent);
  y += ctx.measureText('M').actualBoundingBoxAscent + Math.round(3.5 * MM_TO_PX);
  ctx.fillStyle = '#000000';

  // Location
  ctx.font = `400 ${Math.round(12 * MM_TO_PX / 2.83)}px Arial, Helvetica, sans-serif`;
  ctx.fillStyle = '#555555';
  const locLabelText = t('tire_hotel.label_location_prefix');
  ctx.fillText(locLabelText, PAD, y + ctx.measureText('M').actualBoundingBoxAscent);
  const locLabelWidth = ctx.measureText(locLabelText).width + Math.round(2 * MM_TO_PX);
  ctx.fillStyle = '#000000';
  ctx.font = `800 ${Math.round(24 * MM_TO_PX / 2.83)}px Arial, Helvetica, sans-serif`;
  ctx.fillText(location, PAD + locLabelWidth, y + ctx.measureText('M').actualBoundingBoxAscent);
  y += ctx.measureText('M').actualBoundingBoxAscent + Math.round(2.5 * MM_TO_PX);

  // Storage date
  if (storedDate) {
    ctx.font = `400 ${Math.round(12 * MM_TO_PX / 2.83)}px Arial, Helvetica, sans-serif`;
    ctx.fillStyle = '#555555';
    ctx.fillText(`${t('tire_hotel.label_stored_prefix')} ${storedDate}`, PAD, y + ctx.measureText('M').actualBoundingBoxAscent);
    ctx.fillStyle = '#000000';
  }

  // Workshop name — bottom left
  ctx.font = `400 ${Math.round(11 * MM_TO_PX / 2.83)}px Arial, Helvetica, sans-serif`;
  ctx.fillStyle = '#888888';
  const workshopY = H - PAD;
  ctx.fillText(workshop, PAD, workshopY);
  ctx.fillStyle = '#000000';

  // QR code — bottom right
  try {
    const qrUrl = `${window.location.origin}/tire-hotel/${card.id}`;
    const qrDataUrl = await QRCode.toDataURL(qrUrl, { width: Math.round(12 * MM_TO_PX), margin: 1 });
    const qrImg = new Image();
    await new Promise((resolve, reject) => {
      qrImg.onload = resolve;
      qrImg.onerror = reject;
      qrImg.src = qrDataUrl;
    });
    const qrSize = Math.round(12 * MM_TO_PX);
    ctx.drawImage(qrImg, W - PAD - qrSize, H - PAD - qrSize, qrSize, qrSize);
  } catch (_) { /* QR failed, skip */ }

  // For portrait: rotate the canvas 90° clockwise
  let printCanvas = canvas;
  if (!landscape) {
    const rotated = document.createElement('canvas');
    rotated.width = H;  // 74mm becomes width
    rotated.height = W; // 102mm becomes height
    const rCtx = rotated.getContext('2d');
    rCtx.translate(H, 0);
    rCtx.rotate(Math.PI / 2);
    rCtx.drawImage(canvas, 0, 0);
    printCanvas = rotated;
  }

  const imgDataUrl = printCanvas.toDataURL('image/png');

  // Let the Zebra driver control page size — just fill 100% of whatever it provides
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>${t('tire_hotel.label_print_title')} - ${reg}</title>
<style>
  @page { margin: 0; }
  * { margin: 0; padding: 0; }
  html, body { width: 100%; height: 100%; overflow: hidden; }
  img { width: 100%; height: 100%; display: block; object-fit: contain; }
  @media print { body { -webkit-print-color-adjust: exact; } }
</style>
</head><body>
<img src="${imgDataUrl}" />
</body></html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 200);
  }
}
